// ----------------------------------------------------------------------- imports externos
import { useEffect, useState } from "react";

// ----------------------------------------------------------------------- imports internos
import { prestamosDe } from "../../services/herramientas";
import { duracion, haceCuanto } from "../../shared/herramientas";
import { diaHoraAr } from "../../shared/fechaAr";
import { useAvisarVacio } from "./BloqueFicha";

/* Lo que se le prestó del tablero, en la ficha de la persona.
 *
 * Igna, 15/09: *«debería haber una tarjeta que diga cuándo se le prestó algo. Y
 * lo quiero histórico. Cuando lo devuelva que quede registro de cuándo lo
 * devolvió y cuánto tiempo lo tuvo»*.
 *
 * ── NO HUBO QUE GUARDAR NADA NUEVO ──────────────────────────────────────────
 *
 * `taller_prestamo` escribe `retirado_en`, `devuelto_en` y quién hizo cada cosa
 * desde el primer día. El historial ya existía; lo que no había era dónde
 * mirarlo. El tablero contesta «qué falta de la pared» y esta ficha contesta
 * «qué tiene esta persona», que es la misma tabla leída al revés.
 *
 * ── LO QUE TIENE VA SEPARADO DE LO QUE DEVOLVIÓ ─────────────────────────────
 *
 * Mezclados en una sola lista por fecha, lo que hay que reclamar hoy queda
 * entre medio de lo que ya volvió, y son dos cosas distintas: una es una deuda
 * y la otra es un antecedente. Arriba lo abierto, con «hace cuánto» — que es
 * la pregunta de quien lo está buscando.
 *
 * ── «HACE CUÁNTO» Y «CUÁNTO LA TUVO» NO SON LA MISMA CUENTA ─────────────────
 *
 * La primera mide contra el reloj de ahora y sólo tiene sentido en un préstamo
 * abierto. La segunda mide de retiro a devolución y no depende de cuándo se
 * mire. Con una sola, un préstamo cerrado en marzo diría «hace 180 días»: es
 * cierto, y no es lo que se preguntó.
 *
 * ── NO FRENA LA FICHA ───────────────────────────────────────────────────────
 *
 * Estado propio, igual que `TractorAsignado`: si la consulta falla, la ficha
 * abre lo mismo y este bloque dice que no pudo. «No pude leer» no se dibuja
 * como «no se le prestó nada» — la primera manda a reintentar y la segunda es
 * un dato sobre la persona.
 */
const HerramientasDePersona = ({ dni }) => {
  /* undefined = todavía no llegó · false = no se pudo · {…} = la respuesta. */
  const [datos, setDatos] = useState(undefined);

  useEffect(() => {
    if (!dni) return undefined;
    let vivo = true;
    setDatos(undefined);
    prestamosDe(dni)
      .then((r) => {
        if (!vivo) return;
        setDatos(r.error ? false : r);
      })
      .catch(() => vivo && setDatos(false));
    return () => { vivo = false; };
  }, [dni]);

  /* «Nunca se le prestó nada» esconde el bloque en la ficha de la persona
   * (Igna, 02/10). «No se pudieron leer» NO: es no saber, no un cero. */
  useAvisarVacio(
    datos === undefined ? undefined : datos !== false && !datos.filas.length,
  );

  if (datos === undefined) return <p className="tenue">Consultando…</p>;
  if (datos === false) {
    return (
      <p className="pf-nopude">No se pudo consultar.</p>
    );
  }

  /* #272: lo que se le DIO (definitivo, queda para ella) no es un préstamo
     abierto a reclamar — va en su propio grupo, no en «tiene ahora». */
  const dadas = datos.filas.filter((p) => p.definitivo && !p.devuelto_en);
  const abiertos = datos.filas.filter((p) => !p.definitivo && !p.devuelto_en);
  const cerrados = datos.filas.filter((p) => p.devuelto_en);

  if (!datos.filas.length) {
    return <p className="tenue">Nunca se le prestó una herramienta del tablero.</p>;
  }

  const nombreDe = (p) =>
    p.herramienta?.nombre || p.herramienta?.codigo || "Herramienta";

  return (
    <div className="hdp">
      {dadas.length > 0 && (
        <>
          <p className="hdp-rotulo">
            Se le dieron {dadas.length === 1 ? "" : `(${dadas.length})`}
            <span className="tenue"> · quedan para ella</span>
          </p>
          <ul className="hdp-lista">
            {dadas.map((p) => (
              <li className="hdp-item" key={p.id}>
                <b>{nombreDe(p)}</b>
                <span className="hdp-cuando">
                  el {diaHoraAr(p.retirado_en)}
                  {p.unidad ? ` · interno ${p.unidad}` : ""}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}

      {abiertos.length > 0 && (
        <>
          <p className="hdp-rotulo">
            Tiene ahora {abiertos.length === 1 ? "" : `(${abiertos.length})`}
          </p>
          <ul className="hdp-lista">
            {abiertos.map((p) => (
              <li className="hdp-item hdp-item--abierto" key={p.id}>
                <b>{nombreDe(p)}</b>
                <span className="hdp-cuando">
                  desde el {diaHoraAr(p.retirado_en)}
                  {/* Las horas las calcula la vista del tablero, no esta
                      consulta: acá se cuenta contra el reloj del navegador,
                      que es el que mira el que está leyendo. */}
                  {horasDesde(p.retirado_en) !== null
                    ? ` · ${haceCuanto(horasDesde(p.retirado_en))}`
                    : ""}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}

      {cerrados.length > 0 && (
        <>
          <p className="hdp-rotulo">Devueltas</p>
          <ul className="hdp-lista">
            {cerrados.map((p) => (
              <li className="hdp-item" key={p.id}>
                <b>{nombreDe(p)}</b>
                <span className="hdp-cuando">
                  {diaHoraAr(p.retirado_en)} → {diaHoraAr(p.devuelto_en)}
                  {duracion(p.retirado_en, p.devuelto_en)
                    ? ` · la tuvo ${duracion(p.retirado_en, p.devuelto_en)}`
                    : ""}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}

      {/* Una lista cortada se ve igual que una completa, y lo que faltaría son
          préstamos. Se dice que hay más en vez de dejarlos afuera en silencio. */}
      {datos.hayMas && (
        <p className="tenue pf-chico">
          Se muestran los {datos.filas.length} más recientes. Hay más antes de
          eso.
        </p>
      )}
    </div>
  );
};

/* Horas entre una fecha y ahora. `null` cuando no se puede: `haceCuanto` ya
   sabe no decir nada con eso, y un 0 se leería como «recién». */
function horasDesde(iso) {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return null;
  const h = (Date.now() - t) / 3600000;
  return h >= 0 ? h : null;
}

export default HerramientasDePersona;
